import { MAX_MAP_PLOT_REQUEST_DETAILS } from "@/constants/mapPlotRequest";
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button as CustomButton,
  ButtonVariantOption,
} from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  type PropertySelectionRow,
  type MapPlotRequestDetailAdditionalFields,
} from "../../types";
import { TabPanel } from "./TabPanel";
import { ListSaveTab } from "./ListSaveTab";
import { CsvUploadTab } from "./CsvUploadTab";
import { useCsvUpload } from "./useCsvUpload";
import { FormDataSchema, type FormData } from "./formSchema";
import { useMapPlotImportModal } from "./useMapPlotImportModal";
import { usePropertySelection } from "./usePropertySelection";
import { useCsvUploadForm } from "./useCsvUploadForm";
import { useMapPlotRequest } from "../../hooks/useMapPlotRequest";
import { toast } from "react-toastify";
import { isAxiosError } from "axios";
import { useCreateMapPlotRequest } from "../../hooks/useCreateMapPlotRequest";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useAdditionalFieldsConfig } from "../../hooks/useAdditionalFieldsConfig";
import { convertFormDataToAdditionalFields } from "../../utils/additionalFieldsHelpers";
import { convertTypeEnglishToJapanese } from "../../utils";
import { addressForSave } from "../../utils/realEstateSearch/landAddress";

/**
 * MapPlotImportModalコンポーネントのプロパティ型定義
 */
interface MapPlotImportProps {
  /** モーダルの表示状態 */
  open: boolean;
  /** モーダルを閉じる際のコールバック関数 */
  onClose: () => void;
  /** CSVアップロード成功時のコールバック関数（オプション） */
  onCsvUploadSuccess?: () => void;
  /** CSVアップロード中の状態 */
  csvUploadLoading: boolean;
  /** CSVアップロード状態を設定する関数 */
  setCsvUploadLoading: (loading: boolean) => void;
  /** 選択可能なプロパティのリスト */
  selectedProperties: PropertySelectionRow[];
  /** プロパティリストを更新する関数 */
  setSelectedProperties: React.Dispatch<
    React.SetStateAction<PropertySelectionRow[]>
  >;
  /** ポップアップの「ピンを保存」ボタンから開かれたか */
  openedFromPinSave?: boolean;
}

/**
 * 地図プロットのインポート機能を提供するモーダルコンポーネントです。
 * プロパティリストの保存とCSVファイルのアップロードの2つのタブを提供します。
 *
 * @param props - MapPlotImportPropsで定義されたプロパティ
 * @returns MapPlotImportModalコンポーネント
 */
export const MapPlotImportModal: React.FC<MapPlotImportProps> = ({
  open,
  onClose,
  onCsvUploadSuccess,
  csvUploadLoading,
  setCsvUploadLoading,
  selectedProperties,
  setSelectedProperties,
  openedFromPinSave = false,
}) => {
  const isMobile = useIsMobile();
  const { crm: crmFeatureFlag } = useFeatureFlags();
  const crm = Boolean(crmFeatureFlag);
  const [listSaveLoading, setListSaveLoading] = useState(false);
  const [invalidPropertyIds, setInvalidPropertyIds] = useState<number[]>([]);
  const { availableFields } = useAdditionalFieldsConfig();

  // 追加項目のデフォルト値を生成
  const additionalFieldsDefaultValues = React.useMemo(() => {
    if (!availableFields) return {};
    const defaults: Record<string, string> = {};
    Object.keys(availableFields).forEach((key) => {
      defaults[key] = "";
    });
    return defaults;
  }, [availableFields]);

  const { control, handleSubmit, reset, watch, getValues, setValue } =
    useForm<FormData>({
      mode: "all",
      defaultValues: {
        name: "",
        tags: [],
        additionalFields: additionalFieldsDefaultValues,
        mapPlotRequestId: undefined,
      },
      resolver: zodResolver(FormDataSchema),
    });

  // 既存のマッププロットリクエストを取得（モーダルが開いている時のみ）
  const { mapPlotRequestData, isLoading: isRequestsLoading } =
    useMapPlotRequest(open);
  // 同じ会社で共有された他のユーザーのリストには追加できないため、自分のリストだけを候補にする
  const ownRequests = React.useMemo(
    () =>
      mapPlotRequestData?.filter((request) => request.isEditable !== false) ??
      [],
    [mapPlotRequestData]
  );

  // 既存のタグ一覧を抽出（重複なし）
  const existingTags = React.useMemo(() => {
    const tagNames = ownRequests
      .flatMap((request) => request.tags ?? [])
      .map((tag) => tag.name?.trim())
      .filter((name): name is string => !!name);

    return [...new Set(tagNames)].sort();
  }, [ownRequests]);

  const { tabValue, handleClose, handleTabChange, setResetCsvUpload } =
    useMapPlotImportModal({
      onClose,
      reset,
      openedFromPinSave,
    });

  const csvUploadHook = useCsvUpload({
    onSuccess: onCsvUploadSuccess,
    onClose: handleClose,
    setCsvUploadLoading,
  });

  const {
    fileToCsvUpload,
    showFileErrorMessage,
    handleFileChange,
    uploadCsv,
    resetCsvUpload,
  } = csvUploadHook;

  // CSVアップロードのリセット関数を設定
  setResetCsvUpload?.(resetCsvUpload);

  const { trigger: createMapPlotRequest } = useCreateMapPlotRequest();

  const handleSaveList = async (): Promise<void> => {
    setListSaveLoading(true);

    try {
      const checkedProperties = selectedProperties.filter(
        (prop) => prop.selected
      );

      if (checkedProperties.length > MAX_MAP_PLOT_REQUEST_DETAILS) {
        toast.error(
          `1つの物件リストに保存できる物件は最大${MAX_MAP_PLOT_REQUEST_DETAILS.toLocaleString()}件です`
        );
        return;
      }

      // バリデーション: 座標（緯度・経度）が必要。BE の JSON 保存は座標の無い明細が1件でもあると全体を 400 にする
      // （住所・所在だけの明細はファイル取り込みで登録する）
      const invalidProperties = checkedProperties.filter(
        (prop) => prop.rawData?.lat == null || prop.rawData?.lng == null
      );

      if (invalidProperties.length > 0) {
        const invalidIds = invalidProperties.map((prop) => prop.id);
        setInvalidPropertyIds(invalidIds);
        return;
      }

      // バリデーション成功時は無効なIDをクリア
      setInvalidPropertyIds([]);

      const formValues = getValues();

      // 追加項目を新しい型に変換
      const additionalFieldsData =
        formValues.additionalFields && availableFields
          ? convertFormDataToAdditionalFields(
              formValues.additionalFields,
              availableFields
            )
          : null;

      const requestData = {
        name: formValues.name,
        tags: formValues.tags || [],
        details: checkedProperties.map((prop) => ({
          address: addressForSave(prop),
          latitude: prop.rawData?.lat ?? null,
          longitude: prop.rawData?.lng ?? null,
          location: prop.location,
          chiban: prop.chibanKaokuNumber,
          remarks: prop.remarks ?? "",
          tags: prop.tags ?? [],
          additionalFields:
            prop.additionalFields && prop.additionalFields.trim() !== ""
              ? (JSON.parse(
                  prop.additionalFields
                ) as MapPlotRequestDetailAdditionalFields)
              : {},
          realEstateType: (convertTypeEnglishToJapanese(prop.bookType) ===
          "一棟"
            ? "建物"
            : convertTypeEnglishToJapanese(prop.bookType)) as
            | "土地"
            | "建物"
            | "区分建物",
        })),
        additionalFields: additionalFieldsData
          ? JSON.stringify(additionalFieldsData)
          : undefined,
        ...(formValues.mapPlotRequestId && {
          mapPlotRequestId: formValues.mapPlotRequestId,
        }),
      };

      let errorOccurred = false;

      // API呼び出し
      await createMapPlotRequest(requestData, {
        throwOnError: false,
        // NOTE: App.tsxのSWRConfigに設定されているグローバルonErrorハンドラーを上書き
        // グローバルハンドラーでは「予期しないエラーが発生しました」という汎用メッセージが表示されるが、
        // ここではバックエンドから返されるdetailフィールドの具体的なエラーメッセージを表示したいため、
        // onErrorコールバックを提供してグローバルハンドラーの実行を抑制している
        onError: (error: unknown) => {
          errorOccurred = true;
          // バックエンドのエラーメッセージをそのまま表示
          let errorMessage = "保存に失敗しました";
          if (isAxiosError(error) && error.response?.data) {
            const errorData: unknown = error.response.data;
            if (Array.isArray(errorData)) {
              errorMessage = errorData.join("\n");
            } else if (typeof errorData === "string") {
              errorMessage = errorData;
            }
          }
          toast.error(errorMessage);
        },
      });

      if (!errorOccurred) {
        setSelectedProperties(checkedProperties);
        toast.success("保存しました");
        onClose();
      }
    } finally {
      setListSaveLoading(false);
    }
  };

  const { handleDelete, selectedItems } = usePropertySelection({
    selectedProperties,
    setSelectedProperties,
  });

  const nameValue = watch("name");

  const {
    isButtonDisabled,
    handleFileChangeWrapper,
    handleCsvUploadButtonClick,
  } = useCsvUploadForm({
    fileToCsvUpload,
    csvUploadLoading,
    nameValue,
    handleFileChange,
    uploadCsv,
    handleSubmit,
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleClose();
        }}
        maxWidth={false}
        fullWidth
        fullScreen={isMobile}
        // SP は全画面にするので、PC 用の幅・高さは付けない
        PaperProps={
          isMobile
            ? undefined
            : {
                sx: {
                  minHeight: "70vh",
                  width: "90vw",
                  maxWidth: "1400px",
                  margin: "auto",
                },
              }
        }
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {crm ? "物件リストに保存する" : "ピンを取り込む"}
            {/* 共通 Button は SP で幅100%になるので、内容幅に留める */}
            <Box sx={{ width: "fit-content", flexShrink: 0 }}>
              <CustomButton
                label="閉じる"
                variant={ButtonVariantOption.Outlined}
                onClick={handleClose}
                size={SizingWrapperStyle.SMALL}
                startIcon={<CloseIcon />}
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="一覧を保存" />
              <Tab label="CSVアップロード" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <ListSaveTab
              control={control}
              setValue={setValue}
              watch={watch}
              selectedProperties={selectedProperties}
              setSelectedProperties={setSelectedProperties}
              handleDelete={handleDelete}
              invalidPropertyIds={invalidPropertyIds}
              existingRequests={ownRequests}
              existingTags={existingTags}
              isRequestsLoading={isRequestsLoading}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <CsvUploadTab
              control={control}
              setValue={setValue}
              watch={watch}
              fileToCsvUpload={fileToCsvUpload}
              showFileErrorMessage={showFileErrorMessage}
              handleFileChange={handleFileChangeWrapper}
              existingRequests={ownRequests}
              existingTags={existingTags}
              isRequestsLoading={isRequestsLoading}
            />
          </TabPanel>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={csvUploadLoading || listSaveLoading}
          >
            キャンセル
          </Button>
          {tabValue === 0 ? (
            <Button
              onClick={() => {
                void handleSaveList();
              }}
              variant="contained"
              disabled={
                !nameValue?.trim() ||
                selectedItems.length === 0 ||
                listSaveLoading
              }
            >
              {listSaveLoading ? "保存中..." : "保存する"}
            </Button>
          ) : (
            // 共通 Button は SP で幅100%になり「キャンセル」が押しつぶされるので、内容幅にする
            <Box sx={{ width: "fit-content" }}>
              <CustomButton
                variant={ButtonVariantOption.Contained}
                onClick={handleCsvUploadButtonClick}
                disabled={isButtonDisabled}
                label={csvUploadLoading ? "保存中..." : "保存する"}
              />
            </Box>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
